/**
 * The just-sent bubble, shown before the server answers.
 *
 * Posting a channel message is two round-trips (POST, then the invalidation refetch), so
 * without this the composer clears and the thread shows nothing for that whole window —
 * the "did it send?" gap the legacy convex compose avoided with an optimistic row. We
 * restore it by holding a placeholder in `use-pending-sends.ts` (client state, NOT the
 * query cache — see that file for why) and merging it into the fetched list until the
 * real row arrives.
 *
 * ## Why an `optimistic:` id
 *
 * Every real `Message.id` is a 24-char base36 nanoid. The placeholder takes an id with the
 * `optimistic:` prefix — a shape no server row can have — so it (a) never collides with a
 * real row and (b) is trivially filtered out of the derivations that must only see
 * persisted rows — the read-cursor's `newestMessageId` in particular, which would
 * otherwise POST a placeholder id to `/read`. `ChannelMessageItem` reads the same prefix
 * to render the "sending" state. It is prepended, not sorted, into place: the list is
 * newest-first and the placeholder is always the newest.
 *
 * ## Roots only
 *
 * Only a root send (`replyToId == null`) gets a placeholder: a thread reply is rendered
 * from a separate query by a view that does not merge pending sends. Replies fall back
 * to the post-refetch scroll.
 */

import type {
  ChannelMessage,
  ChannelQuotedMessage,
  CreateChannelMessageInput,
} from "../_api/channel-endpoints";

/** A placeholder and, once the POST has answered, the id its real row will carry. */
export type PendingSend = {
  message: ChannelMessage;
  /** Set by `markPendingSent`. Absent while the POST is still in flight. */
  sentId?: string;
};

const OPTIMISTIC_ID_PREFIX = "optimistic:";

/** The placeholder id for the `tempId`-th unsent message of this session. */
export function optimisticMessageId(tempId: number): string {
  return `${OPTIMISTIC_ID_PREFIX}${tempId}`;
}

/** True for a placeholder produced by {@link buildOptimisticMessage}. */
export function isOptimisticMessage(message: { id: string }): boolean {
  return message.id.startsWith(OPTIMISTIC_ID_PREFIX);
}

export type BuildOptimisticMessageInput = {
  tempId: number;
  authorId: string;
  input: CreateChannelMessageInput;
  /** The message the send quotes, so the placeholder already shows its card. */
  quotedMessage?: ChannelQuotedMessage | null;
  /** ISO string — the action's clock, passed in so this stays a calculation. */
  nowIso: string;
};

/**
 * A minimal, render-safe `ChannelMessage` placeholder.
 *
 * Only the fields `ChannelMessageItem` / `groupMessagesByDay` actually read are set; the
 * cast is the seam where a client-only row stands in for a server one until the refetch.
 * Attachments are intentionally empty — their ids are not resolved rows yet, so the
 * placeholder shows text only and the real bubble (with thumbnails) arrives on refetch.
 */
export function buildOptimisticMessage({
  tempId,
  authorId,
  input,
  quotedMessage = null,
  nowIso,
}: BuildOptimisticMessageInput): ChannelMessage {
  // `as unknown as` because this is a deliberate partial: only the fields the renderer
  // reads are set, and the row is discarded the moment the refetch lands. The remaining
  // `ChannelMessage` fields (userReads, linkPreview, …) go unread on this path.
  return {
    id: optimisticMessageId(tempId),
    oldId: null,
    userCreatedId: authorId,
    body: input.comment ?? "",
    status: "PENDING",
    createdAt: nowIso,
    updatedAt: nowIso,
    viaClient: "WEBAPP",
    pinnedAt: null,
    replyToId: null,
    quotedMessage,
    attachments: [],
    blocks: [],
    reactions: [],
    // Read by the unread rule. A viewer's own send is already excluded from unread by
    // author id, so this only keeps the placeholder honest against the shape it claims.
    authoredByBot: false,
  } as unknown as ChannelMessage;
}

/**
 * Is this placeholder still the only copy of its message on screen?
 *
 * True until the fetched list carries the id the POST answered with. A placeholder
 * whose POST has not answered yet (`sentId` unset) is always still needed — which is
 * what keeps a burst of sends intact: one message landing says nothing about the
 * others, so only the message that landed stops being a placeholder.
 */
function isStillUnseen(entry: PendingSend, fetchedIds: Set<string>): boolean {
  return entry.sentId == null || !fetchedIds.has(entry.sentId);
}

function fetchedIdsOf(messages: ChannelMessage[]): Set<string> {
  return new Set(messages.map((message) => message.id));
}

/**
 * Append the still-unseen placeholders to a fetched, OLDEST-FIRST message list.
 *
 * The handover from placeholder to real row happens inside one render — the entry
 * stops being merged in the same pass that first shows the fetched row — so no frame
 * shows the message twice and none shows it missing.
 *
 * Returns `messages` itself when nothing is pending, so the common case adds no new
 * array identity for React to diff.
 */
export function mergePendingMessages(
  messages: ChannelMessage[],
  pending: PendingSend[],
): ChannelMessage[] {
  if (pending.length === 0) return messages;

  const fetchedIds = fetchedIdsOf(messages);
  const unseen = pending.filter((entry) => isStillUnseen(entry, fetchedIds));
  if (unseen.length === 0) return messages;

  return [...messages, ...unseen.map((entry) => entry.message)];
}

/**
 * The registry entries still worth keeping, given what the thread has fetched.
 *
 * The counterpart to {@link mergePendingMessages}: what that stops rendering, this
 * stops storing. Returns the SAME array when nothing landed, so the registry can tell
 * a no-op from a change without diffing.
 */
export function dropLandedPendingSends(
  pending: PendingSend[],
  messages: ChannelMessage[],
): PendingSend[] {
  if (pending.length === 0) return pending;

  const fetchedIds = fetchedIdsOf(messages);
  const unseen = pending.filter((entry) => isStillUnseen(entry, fetchedIds));
  return unseen.length === pending.length ? pending : unseen;
}
